import React, {useEffect, useCallback, useRef, useState} from 'react';

import {observer} from 'mobx-react';
import Dialog from '@mui/material/Dialog';
import CloseIcon from '@mui/icons-material/Close';

import {useStores} from '../../../Hooks';
import ContentTree from '../../Generic/ContentTree/ContentTree';
import useAccessModal from './useAccessModal';
import useNodeRenderers from './useNodeRenderers';
import InfoBanner from './InfoBanner';
import AccessFooter from './AccessFooter';
import SearchBar from './SearchBar';

const AccessModal = observer(() => {
    const {modalStore, generalStore} = useStores();
    const modalData = modalStore.isAccessModalOpen;
    const isFilterMode = !!modalData?.filterMode;
    const [isSaving, setIsSaving] = useState(false);
    const accessModal = useAccessModal(generalStore.workspace, {
        targetId: isFilterMode ? undefined : modalData?.targetId,
        targetIds: isFilterMode ? undefined : modalData?.targetIds,
        targetType: isFilterMode ? undefined : modalData?.targetType,
        groups: isFilterMode ? [] : (modalData?.groups || []),
        targets: isFilterMode ? [] : (modalData?.targets || []),
        isRoleVisibilityEnabled: !!generalStore.domainSettings?.IsRoleVisibilityEnabled,
        savedState: modalData?.initialValue || null,
        accessSummary: modalData?.accessSummary || null,
    });
    const {
        tree, checkboxState, getAccessInfo, handleCheckToggle, handleToggleAll,
        isAllSelected, initializeModal, resetModal, buildSavePayload, buildFilterPayload, selectionSummary, hasEdits,
        isBulkMode, mixed, edits, targets, groupMembership, isItemDisabled, itemDisabledReason,
        isSharedEffective, isSearchActive, handleSearch,
    } = accessModal;
    const initRef = useRef(null);

    const getDisabledTooltip = useCallback((nodeId) => {
        const reason = itemDisabledReason(nodeId);
        if (reason === 'unpublished') return 'Item is not published';
        return generalStore.translate('itemNotSupportedByUserRole') || 'This item is not available for the selected user role.';
    }, [itemDisabledReason, generalStore]);

    const {renderNodePrefix, renderNodeSuffix, getNodeClassName, getNodeTooltip, getNodeIcon} = useNodeRenderers({
        checkboxState, handleCheckToggle, getAccessInfo, isItemDisabled, isSharedEffective,
        isBulkMode, isFilterMode, mixed, edits, targets, groupMembership, getDisabledTooltip,
    });

    useEffect(() => {
        if (modalData && initRef.current !== modalData) {
            initRef.current = modalData;
            initializeModal();
        }
    }, [modalData]); // eslint-disable-line react-hooks/exhaustive-deps

    const handleClose = useCallback(() => {
        initRef.current = null;
        modalStore.isAccessModalOpen = false;
        resetModal();
    }, [modalStore, resetModal]);

    const handleSave = useCallback(async () => {
        if (isFilterMode) {
            const payload = buildFilterPayload();
            modalData.onFilterSave(payload);
            handleClose();
            return;
        }
        if (!modalData?.onSave) {
            handleClose();
            return;
        }
        setIsSaving(true);
        try {
            const payload = buildSavePayload();
            await modalData.onSave(payload);
            handleClose();
        } catch (error) {
            console.error('Failed to save access:', error);
        }
        setIsSaving(false);
    }, [modalData, isFilterMode, buildSavePayload, buildFilterPayload, handleClose]);

    const title = isFilterMode
        ? (generalStore.translate('filterByAccess') || 'Filter by Access')
        : isBulkMode
            ? `${generalStore.translate('editAccess') || 'Edit Access'} (${targets.length} ${generalStore.translate('members') || 'members'})`
            : (generalStore.translate('selectAccess') || 'Select Access');

    return (
        <Dialog
            open={!!modalData}
            onClose={handleClose}
            maxWidth={false}
            PaperProps={{
                className: 'rounded-lg',
                style: {width: 480, height: '70vh', display: 'flex', flexDirection: 'column'},
            }}
        >
            <div className="flex items-center justify-between px-4 py-2.5">
                <span className="text-base font-semibold text-main-normal">{title}</span>
                <CloseIcon
                    onClick={handleClose}
                    style={{fontSize: 18, cursor: 'pointer', color: 'var(--main-light-color)'}}
                />
            </div>

            {!isFilterMode && <InfoBanner isBulkMode={isBulkMode} />}

            <SearchBar key={modalData?.targetId || modalData?.targetIds?.join()} onSearch={handleSearch} placeholder={generalStore.translate('searchContent') || 'Search content...'} />

            <div data-testid="access-select-all" className="flex items-center mx-4 px-2 py-1.5 mb-1 rounded-menu cursor-pointer hover:bg-panels-normal" onClick={handleToggleAll}>
                <span className="text-sm text-link-normal">
                    {isAllSelected
                        ? (generalStore.translate('unselectAll') || 'Unselect All')
                        : (generalStore.translate('selectAll') || 'Select All')
                    }
                </span>
            </div>

            <div className="flex-1 overflow-y-auto mx-4 mb-2 p-1 rounded-menu border border-panels-normal bg-windowsandmenus-normal">
                <ContentTree tree={tree} renderNodePrefix={renderNodePrefix} renderNodeSuffix={renderNodeSuffix} getNodeClassName={getNodeClassName} getNodeTooltip={getNodeTooltip} getNodeIcon={getNodeIcon} />
            </div>

            <AccessFooter
                summary={selectionSummary}
                onCancel={handleClose}
                onSave={handleSave}
                isSaving={isSaving}
                saveLabel={isFilterMode ? (generalStore.translate('apply') || 'Apply') : undefined}
                showSummary={false}
                hasEdits={hasEdits}
                disableSave={!isFilterMode && !hasEdits}
            />
        </Dialog>
    );
});

export default AccessModal;
